import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, User, Lock, Check, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * InviteAccept — /kutsu/:token
 *
 * Kolmen vaiheen käyttöönotto:
 *   1. Ladataan kutsun esikatselu (yritys, kutsuja, sähköposti).
 *   2. Käyttäjä valitsee nimensä + salasanan.
 *   3. Backend luo tilin, palauttaa JWT:n → kirjaudumme sisään ja ohjaamme
 *      etusivulle (/app on toistaiseksi kaikkien kotisivu; R2B:ssä lisätään
 *      kenttäkäyttäjän oma etusivu).
 */
export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | ready | expired | invalid | submitting | done
  const [invite, setInvite] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invites/${token}`);
        setInvite(data);
        setStatus("ready");
      } catch (e) {
        if (e?.response?.status === 410) {
          setStatus("expired");
        } else {
          setStatus("invalid");
          setErrorMsg(e?.response?.data?.detail || "Kutsu ei ole voimassa");
        }
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Salasanan on oltava vähintään 8 merkkiä");
      return;
    }
    setStatus("submitting");
    try {
      const { data } = await axios.post(`${API}/invites/${token}/accept`, { name: name.trim(), password });
      // Persist session same way as normal login.
      if (setSession) setSession(data);
      else {
        // Fallback: write to localStorage keys used by AuthContext.
        try { localStorage.setItem("veraso_token", data.token); } catch { /* noop */ }
      }
      toast.success("Tervetuloa Verasoon!");
      setStatus("done");
      // Small delay so the user sees the success state before redirect.
      setTimeout(() => navigate("/kentta", { replace: true }), 800);
    } catch (e) {
      setStatus("ready");
      const msg = e?.response?.data?.detail || "Rekisteröinti epäonnistui";
      toast.error(typeof msg === "string" ? msg : "Rekisteröinti epäonnistui");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-baseline gap-1 font-display font-bold tracking-tight text-2xl">
            <span className="text-blue-900">Veraso</span>
            <span className="text-emerald-600">Reports</span>
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-slate-200 shadow-xl shadow-slate-900/5 p-6 md:p-8" data-testid="invite-accept-card">
          {status === "loading" && (
            <div className="flex items-center gap-3 text-slate-600 py-6 justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
              <span className="text-sm">Ladataan kutsua…</span>
            </div>
          )}

          {status === "expired" && (
            <div className="text-center py-6" data-testid="invite-expired">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 ring-1 ring-amber-100 mb-3">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h1 className="font-display text-xl font-semibold text-slate-900">Kutsu on vanhentunut</h1>
              <p className="text-sm text-slate-600 mt-2">Pyydä pääkäyttäjää lähettämään uusi kutsu.</p>
            </div>
          )}

          {status === "invalid" && (
            <div className="text-center py-6" data-testid="invite-invalid">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 ring-1 ring-rose-100 mb-3">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h1 className="font-display text-xl font-semibold text-slate-900">Kutsu ei ole voimassa</h1>
              <p className="text-sm text-slate-600 mt-2">{errorMsg}</p>
            </div>
          )}

          {(status === "ready" || status === "submitting") && invite && (
            <>
              <div className="mb-5">
                <div className="text-[11px] uppercase tracking-widest text-emerald-600 font-semibold">Kutsu tiimiin</div>
                <h1 className="font-display text-xl md:text-2xl font-semibold text-slate-900 mt-1 leading-tight">
                  {invite.inviter_name} kutsuu sinut käyttämään<br />
                  <span className="text-emerald-700">{invite.company_name}</span>-tiimiä
                </h1>
                <div className="mt-3 inline-flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5">
                  <Mail className="h-3.5 w-3.5 text-slate-400" />
                  {invite.email}
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label htmlFor="invite-name" className="block text-sm font-semibold text-slate-700 mb-1.5">Nimesi</label>
                  <div className="relative">
                    <User className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="invite-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Etunimi Sukunimi"
                      required
                      className="input-base pl-10 w-full"
                      data-testid="invite-name-input"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="invite-password" className="block text-sm font-semibold text-slate-700 mb-1.5">Valitse salasana <span className="text-slate-400 font-normal">(vähintään 8 merkkiä)</span></label>
                  <div className="relative">
                    <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="invite-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={8}
                      required
                      className="input-base pl-10 w-full"
                      data-testid="invite-password-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3 text-sm disabled:opacity-50 shadow-sm"
                  data-testid="invite-accept-submit"
                >
                  {status === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Luo tili ja aloita
                </button>
              </form>
            </>
          )}

          {status === "done" && (
            <div className="text-center py-6" data-testid="invite-done">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 mb-3">
                <Check className="h-6 w-6" />
              </div>
              <h1 className="font-display text-xl font-semibold text-slate-900">Tervetuloa!</h1>
              <p className="text-sm text-slate-600 mt-2">Ohjataan sinut nyt Verasoon…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
